import { useEffect, useState } from 'react';
import { notification } from 'antd';

import { isCandidateCvProcessingActive, isCandidateCvProcessingFailure } from '../candidate-ai/candidateAiReviewState';
import { CandidateAiReviewStateType } from '../candidates/Candidates.types';

const CV_PROCESSING_POLL_INTERVAL_MS = 3000;
const CV_PROCESSING_POLL_TIMEOUT_MS = 120000;

type CandidateAiRefreshSession = {
  baselineAiCvHash: string | null;
  baselineProcessingCvHash: string | null;
  baselineHasSuggestions: boolean;
  baselinePendingCount: number;
  hasObservedProcessingStart: boolean;
  startedAt: number;
};

type UseCandidateAiRefreshAfterCvUploadProps = {
  aiReviewState?: CandidateAiReviewStateType;
  refetchAiReviewState: () => Promise<unknown>;
};

const buildCandidateAiRefreshSession = (aiReviewState: CandidateAiReviewStateType): CandidateAiRefreshSession => ({
  baselineAiCvHash: aiReviewState.aiSuggestions.cvHash ?? null,
  baselineProcessingCvHash: aiReviewState.cvProcessing.cvHash ?? null,
  baselineHasSuggestions: aiReviewState.aiSuggestions.hasSuggestions,
  baselinePendingCount: aiReviewState.aiSuggestions.pendingCount,
  hasObservedProcessingStart: false,
  startedAt: Date.now(),
});

const hasFreshAiSuggestions = (aiReviewState: CandidateAiReviewStateType, session: CandidateAiRefreshSession) =>
  (aiReviewState.aiSuggestions.cvHash ?? null) !== session.baselineAiCvHash ||
  aiReviewState.aiSuggestions.pendingCount !== session.baselinePendingCount ||
  aiReviewState.aiSuggestions.hasSuggestions !== session.baselineHasSuggestions;

const hasCandidateAiProcessingStarted = (aiReviewState: CandidateAiReviewStateType, session: CandidateAiRefreshSession) =>
  isCandidateCvProcessingActive(aiReviewState.cvProcessing.status) ||
  (aiReviewState.cvProcessing.cvHash ?? null) !== session.baselineProcessingCvHash ||
  (aiReviewState.aiSuggestions.cvHash ?? null) !== session.baselineAiCvHash;

export const useCandidateAiRefreshAfterCvUpload = ({
  aiReviewState,
  refetchAiReviewState,
}: UseCandidateAiRefreshAfterCvUploadProps) => {
  const [candidateAiRefreshSession, setCandidateAiRefreshSession] = useState<CandidateAiRefreshSession | null>(null);

  useEffect(() => {
    if (!candidateAiRefreshSession) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setCandidateAiRefreshSession((currentSession) => {
        if (!currentSession) {
          return currentSession;
        }

        if (Date.now() - currentSession.startedAt > CV_PROCESSING_POLL_TIMEOUT_MS) {
          return null;
        }

        void refetchAiReviewState();
        return currentSession;
      });
    }, CV_PROCESSING_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [candidateAiRefreshSession, refetchAiReviewState]);

  useEffect(() => {
    if (!candidateAiRefreshSession || !aiReviewState) {
      return;
    }

    const processingStatus = aiReviewState.cvProcessing.status;
    const canProcess = aiReviewState.cvProcessing.canProcess;

    if (canProcess === false) {
      setCandidateAiRefreshSession(null);
      return;
    }

    const processingStarted =
      candidateAiRefreshSession.hasObservedProcessingStart ||
      hasCandidateAiProcessingStarted(aiReviewState, candidateAiRefreshSession);

    if (processingStarted !== candidateAiRefreshSession.hasObservedProcessingStart) {
      setCandidateAiRefreshSession((currentSession) =>
        currentSession ? { ...currentSession, hasObservedProcessingStart: true } : currentSession
      );
    }

    if (!processingStarted) {
      return;
    }

    if (isCandidateCvProcessingActive(processingStatus) || processingStatus === null) {
      return;
    }

    if (processingStatus === 'READY') {
      if (hasFreshAiSuggestions(aiReviewState, candidateAiRefreshSession) && aiReviewState.aiSuggestions.hasSuggestions) {
        notification.success({
          message: 'New CV suggestions are ready to review.',
          description: 'Open CV suggestions to review them.',
        });
      } else {
        notification.info({
          message: 'Finished reading the CV.',
          description: 'No new CV suggestions were found.',
        });
      }

      setCandidateAiRefreshSession(null);
      return;
    }

    if (isCandidateCvProcessingFailure(processingStatus)) {
      notification.warning({
        message: "CV couldn't be read.",
        description: 'Try uploading the CV again if CV suggestions do not appear.',
      });
    }

    setCandidateAiRefreshSession(null);
  }, [aiReviewState, candidateAiRefreshSession]);

  return {
    isAiSuggestionsProcessing: candidateAiRefreshSession !== null,
    startCandidateAiRefreshSession: (currentAiReviewState: CandidateAiReviewStateType) => {
      setCandidateAiRefreshSession(buildCandidateAiRefreshSession(currentAiReviewState));
    },
  };
};
